import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { zoneCategories, zoneArticles, siteConfig } from '@/mocks/siteData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = zoneCategories[slug as keyof typeof zoneCategories];
  const articles = zoneArticles[slug as keyof typeof zoneArticles] || [];

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900">分类未找到</h1>
          <Link to="/" className="text-primary-600 hover:underline mt-4 inline-block cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: `${category.name} - ${siteConfig.name}`,
        url: `${siteConfig.url}/category/${slug}`,
        description: category.description,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, item: { '@id': siteConfig.url, name: '首页' } },
          { '@type': 'ListItem', position: 2, item: { '@id': `${siteConfig.url}/category/${slug}`, name: category.name } },
        ],
      },
    ],
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: category.name },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(categoryJsonLd)}</script>
      <Navbar />
      <main>
        <section className="pt-24 pb-8 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              {category.name}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base mb-8">{category.description}</p>

            <div className="flex flex-wrap gap-3">
              {category.subcategories.map((sub) => (
                <button
                  key={sub.name}
                  className="px-4 py-2 bg-background-50 border border-background-200 rounded-full text-sm text-foreground-700 whitespace-nowrap hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer font-label"
                >
                  {sub.name}
                  <span className="ml-1.5 text-xs text-foreground-400">({sub.count})</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {article.views.toLocaleString()} 浏览
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}